import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getVoterToken } from '@/lib/voterToken';

export interface ChoiceReason {
  id: string;
  slug: string;
  label: string;
  icon: string;
  sort_order: number;
}

export interface ReasonAggregate {
  reason: ChoiceReason;
  count: number;
  voted: boolean;
}

export interface VariantReasonVotesData {
  reasons: ReasonAggregate[];
  totalVoters: number;
}

export function useVariantReasonVotes(variantId: string | undefined) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id ?? null;
  const token = getVoterToken();

  const query = useQuery({
    queryKey: ['variant-reason-votes', variantId, userId, token],
    enabled: !!variantId,
    staleTime: 30_000,
    queryFn: async (): Promise<VariantReasonVotesData> => {
      if (!variantId) return { reasons: [], totalVoters: 0 };

      const [{ data: reasons }, { data: summary }] = await Promise.all([
        supabase
          .from('variant_choice_reasons' as any)
          .select('id, slug, label, icon, sort_order')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        (supabase as any).rpc('get_variant_reason_vote_summary', {
          _variant_id: variantId,
          _voter_token: userId ? null : token,
        }),
      ]);

      const reasonList = ((reasons || []) as unknown) as ChoiceReason[];
      const rows = ((summary || []) as unknown) as {
        reason_id: string;
        count: number;
        voted_by_me: boolean;
        total_voters: number;
      }[];

      const countByReason = new Map<string, number>();
      const myReasons = new Set<string>();
      let totalVoters = 0;
      for (const r of rows) {
        countByReason.set(r.reason_id, r.count);
        if (r.voted_by_me) myReasons.add(r.reason_id);
        totalVoters = r.total_voters ?? totalVoters;
      }

      const aggregates: ReasonAggregate[] = reasonList.map((r) => ({
        reason: r,
        count: countByReason.get(r.id) || 0,
        voted: myReasons.has(r.id),
      }));

      return { reasons: aggregates, totalVoters };
    },
  });

  const mutation = useMutation({
    mutationFn: async (reasonSlug: string) => {
      const { error } = await (supabase as any).rpc('toggle_reason_vote', {
        _variant_id: variantId,
        _reason_slug: reasonSlug,
        _voter_token: userId ? null : token,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['variant-reason-votes', variantId] });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    toggle: mutation.mutate,
    isPending: mutation.isPending,
  };
}
